import React, { useEffect, useState } from "react";
import { Link } from "react-router";
import "./ConsentBanner.css";
import {
  ConsentPreferences,
  DeclaredVendors,
  OPEN_SETTINGS_EVENT,
  needsPrompt,
  readConsent,
  saveConsent,
} from "./consent";

interface ConsentBannerProps {
  // A Tag Manager container is active, so there is something to consent to.
  gtmActive: boolean;
  declared: DeclaredVendors;
  // The admin's draft preview: the banner renders, but never persists.
  isPreview: boolean;
}

type CommitMethod = "accept_all" | "reject_optional" | "save_custom";

const NOTHING: ConsentPreferences = { analytics: false, marketing: false };

// The cookie banner. Non-blocking: the page stays usable behind it, and no
// optional tag fires until a choice is made because the server's inline script
// set Consent Mode to denied before Tag Manager loaded.
const ConsentBanner: React.FC<ConsentBannerProps> = ({ gtmActive, declared, isPreview }) => {
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState(false);
  const [prefs, setPrefs] = useState<ConsentPreferences>(NOTHING);

  // Decide on mount (and when the declared vendors change) whether to ask.
  // The stored choice is read here, not during render, so the server-rendered
  // document and the first client render agree.
  useEffect(() => {
    if (!gtmActive) return;
    const stored = readConsent();
    setPrefs(stored ? { analytics: stored.analytics, marketing: stored.marketing } : NOTHING);
    setOpen(needsPrompt(stored, declared));
    setDetails(false);
  }, [gtmActive, declared]);

  useEffect(() => {
    if (!gtmActive) return;
    const reopen = () => {
      const stored = readConsent();
      setPrefs(stored ? { analytics: stored.analytics, marketing: stored.marketing } : NOTHING);
      setDetails(true);
      setOpen(true);
    };
    window.addEventListener(OPEN_SETTINGS_EVENT, reopen);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, reopen);
  }, [gtmActive]);

  if (!gtmActive || !open) return null;

  const commit = (choice: ConsentPreferences, method: CommitMethod) => {
    // Save first so Consent Mode is already updated when the event below
    // reaches the container; a gated tag reacting to it then sees "granted".
    saveConsent(choice, declared, { persist: !isPreview });
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "consent_choice",
      analytics_granted: choice.analytics,
      marketing_granted: choice.marketing,
      method,
    });
    setPrefs(choice);
    setOpen(false);
  };

  return (
    <section className="consent-banner" role="dialog" aria-labelledby="consent-title" aria-describedby="consent-text">
      <h2 id="consent-title">Evästeet tällä sivustolla</h2>
      <p id="consent-text">
        Käytämme välttämättömiä evästeitä sivuston toimintaan. Kävijämittaus ja markkinointi ovat käytössä
        vain suostumuksellasi. Voit muuttaa valintaasi milloin tahansa.{" "}
        <Link to="/evastekaytanto">Lue evästekäytäntö</Link>
      </p>
      {details && (
        <ul className="consent-options">
          <li className="consent-option">
            <span className="consent-option-name">Välttämättömät</span>
            <button type="button" role="switch" aria-checked="true" className="consent-switch" disabled aria-label="Välttämättömät, aina käytössä" />
            <span className="consent-option-description">Sivuston perustoiminnot ja evästevalintasi muistaminen. Aina käytössä.</span>
          </li>
          <li className="consent-option">
            <span className="consent-option-name">Kävijämittaus</span>
            <button
              type="button"
              role="switch"
              aria-checked={prefs.analytics}
              className="consent-switch"
              aria-label="Kävijämittaus"
              onClick={() => setPrefs({ ...prefs, analytics: !prefs.analytics })}
            />
            <span className="consent-option-description">Tilastotietoa sivuston käytöstä sivuston kehittämiseen.</span>
          </li>
          <li className="consent-option">
            <span className="consent-option-name">Markkinointi</span>
            <button
              type="button"
              role="switch"
              aria-checked={prefs.marketing}
              className="consent-switch"
              aria-label="Markkinointi"
              onClick={() => setPrefs({ ...prefs, marketing: !prefs.marketing })}
            />
            <span className="consent-option-description">Mainonnan kohdentaminen ja sen tuloksellisuuden mittaaminen.</span>
          </li>
        </ul>
      )}
      <div className="consent-actions">
        <button type="button" onClick={() => commit(NOTHING, "reject_optional")}>
          Hylkää valinnaiset
        </button>
        {details ? (
          <button type="button" onClick={() => commit(prefs, "save_custom")}>
            Tallenna valinnat
          </button>
        ) : (
          <button type="button" onClick={() => setDetails(true)}>
            Asetukset
          </button>
        )}
        <button type="button" className="consent-accept" onClick={() => commit({ analytics: true, marketing: true }, "accept_all")}>
          Hyväksy kaikki
        </button>
      </div>
    </section>
  );
};

export default ConsentBanner;
